import { useState, useEffect, useCallback, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useTranslation } from '../../hooks/useTranslation';
import {
  AdminPageHeader,
  AdminStatCard,
  AdminEmptyState,
  type AdminTableColumn,
} from '../../components/admin';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import type { ErrorLogEntry } from '@flymind/shared';

/* ---------- Types ---------- */

// api.get() extracts .data from ApiResponse, so we receive the array directly
type ErrorList = ErrorLogEntry[];

interface ErrorStatGroup {
  source: string;
  severity: string;
  count: number;
}

interface ErrorStats {
  last7Days: ErrorStatGroup[];
  last30Days: ErrorStatGroup[];
}

/* ---------- Constants ---------- */

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  error: 'bg-orange-100 text-orange-700',
  warning: 'bg-yellow-100 text-yellow-700',
  info: 'bg-blue-100 text-blue-700',
};

/* ---------- Helpers ---------- */

function sumCounts(groups: ErrorStatGroup[]): number {
  return groups.reduce((acc, g) => acc + g.count, 0);
}

function countBySeverity(groups: ErrorStatGroup[], severity: string): number {
  return groups.filter((g) => g.severity === severity).reduce((acc, g) => acc + g.count, 0);
}

/* ---------- Component ---------- */

export default function AdminErrors() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin, adminApi } = useAdmin();
  const { t, locale } = useTranslation();
  const dateLocale = locale === 'en' ? 'en-US' : 'it-IT';

  const SEVERITY_LABELS: Record<string, string> = {
    critical: t('admin.errors.severityCritical'),
    error: t('admin.errors.severityError'),
    warning: t('admin.errors.severityWarning'),
    info: t('admin.errors.severityInfo'),
  };

  const SOURCE_OPTIONS = [
    { value: '', label: t('admin.errors.filterAllSources') },
    { value: 'api', label: t('admin.errors.sourceApi') },
    { value: 'worker', label: t('admin.errors.sourceWorker') },
    { value: 'webhook', label: t('admin.errors.sourceWebhook') },
    { value: 'cron', label: t('admin.errors.sourceCron') },
    { value: 'auth', label: t('admin.errors.sourceAuth') },
  ];

  const SEVERITY_OPTIONS = [
    { value: '', label: t('admin.errors.filterAllSeverities') },
    { value: 'critical', label: t('admin.errors.severityCritical') },
    { value: 'error', label: t('admin.errors.severityError') },
    { value: 'warning', label: t('admin.errors.severityWarning') },
    { value: 'info', label: t('admin.errors.severityInfo') },
  ];

  const [data, setData] = useState<ErrorList | null>(null);
  const [stats, setStats] = useState<ErrorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Clean-up dialog
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  // Filters from URL
  const sourceFilter = searchParams.get('source') || '';
  const severityFilter = searchParams.get('severity') || '';
  const fromFilter = searchParams.get('from') || '';
  const toFilter = searchParams.get('to') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        if (key !== 'page') {
          next.delete('page');
        }
        return next;
      });
    },
    [setSearchParams],
  );

  /* ---------- Fetch errors ---------- */

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', '20');
      if (sourceFilter) params.set('source', sourceFilter);
      if (severityFilter) params.set('severity', severityFilter);
      if (fromFilter) params.set('from', fromFilter);
      if (toFilter) params.set('to', toFilter);

      const result = await adminApi.get<ErrorLogEntry[]>(
        `/errors?${params.toString()}`,
      );
      setData(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Errore nel caricamento log errori:', err);
    } finally {
      setLoading(false);
    }
  }, [adminApi, page, sourceFilter, severityFilter, fromFilter, toFilter]);

  /* ---------- Fetch stats ---------- */

  const fetchStats = useCallback(async () => {
    try {
      const result = await adminApi.get<ErrorStats>('/errors/stats');
      setStats(result);
    } catch (err) {
      console.error('Errore nel caricamento statistiche errori:', err);
    }
  }, [adminApi]);

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  /* ---------- Cleanup ---------- */

  const handleCleanup = async () => {
    setCleanupLoading(true);
    try {
      await adminApi.delete('/errors?olderThanDays=30');
      setCleanupOpen(false);
      await Promise.all([fetchErrors(), fetchStats()]);
    } catch (err) {
      console.error('Errore nella pulizia log:', err);
    } finally {
      setCleanupLoading(false);
    }
  };

  /* ---------- Table columns ---------- */

  const columns: AdminTableColumn<ErrorLogEntry>[] = [
    {
      key: 'severity',
      label: t('admin.errors.colSeverity'),
      render: (row) => (
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${SEVERITY_COLORS[row.severity] || 'bg-gray-100 text-gray-700'}`}
        >
          {SEVERITY_LABELS[row.severity] || row.severity}
        </span>
      ),
    },
    {
      key: 'source',
      label: t('admin.errors.colSource'),
      render: (row) => (
        <span className="font-medium capitalize">{row.source}</span>
      ),
    },
    {
      key: 'message',
      label: t('admin.errors.colMessage'),
      render: (row) => (
        <span className="max-w-md truncate block text-text-secondary" title={row.message}>
          {row.message}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: t('admin.errors.colDate'),
      render: (row) =>
        new Date(row.createdAt).toLocaleString(dateLocale, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
  ];

  const items = data ?? [];
  const total = items.length;
  const totalPages = 1;

  return (
    <div>
      <AdminPageHeader
        title={t('admin.errors.title')}
        subtitle={data ? t('admin.errors.foundCount').replace('{count}', String(total)) : undefined}
        actions={
          isSuperAdmin ? (
            <Button variant="danger" size="sm" onClick={() => setCleanupOpen(true)}>
              {t('admin.errors.cleanOldLogs')}
            </Button>
          ) : undefined
        }
      />

      {/* Stats */}
      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <AdminStatCard
            label={t('admin.errors.stats7d')}
            value={sumCounts(stats.last7Days)}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
            }
          />
          <AdminStatCard
            label={t('admin.errors.stats30d')}
            value={sumCounts(stats.last30Days)}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
              </svg>
            }
          />
          <AdminStatCard
            label={t('admin.errors.statsCritical7d')}
            value={countBySeverity(stats.last7Days, 'critical')}
            trend={countBySeverity(stats.last7Days, 'critical') > 0 ? 'up' : undefined}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75h-.152c-3.196 0-6.1-1.25-8.25-3.286ZM12 15.75h.007v.008H12v-.008Z" />
              </svg>
            }
          />
          <AdminStatCard
            label={t('admin.errors.statsErrors7d')}
            value={countBySeverity(stats.last7Days, 'error')}
            icon={
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0 1 12 12.75ZM12 12.75c-1.148 0-2.278-.08-3.383-.237C7.58 12.367 6.75 11.546 6.75 10.5c0-3.728 2.35-6.75 5.25-6.75s5.25 3.022 5.25 6.75c0 1.047-.83 1.867-1.866 2.013A24.204 24.204 0 0 1 12 12.75Z" />
              </svg>
            }
          />
        </div>
      )}

      {/* Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
        <select
          value={sourceFilter}
          onChange={(e) => setParam('source', e.target.value)}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          {SOURCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={severityFilter}
          onChange={(e) => setParam('severity', e.target.value)}
          className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          {SEVERITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2">
          <label className="text-sm text-text-secondary">{t('admin.errors.dateFromLabel')}</label>
          <input
            type="date"
            value={fromFilter}
            onChange={(e) => setParam('from', e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-sm text-text-secondary">{t('admin.errors.dateToLabel')}</label>
          <input
            type="date"
            value={toFilter}
            onChange={(e) => setParam('to', e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Table */}
      {!loading && items.length === 0 ? (
        <AdminEmptyState
          title={t('admin.errors.emptyTitle')}
          description={t('admin.errors.emptyDescription')}
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-surface">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead>
              <tr className="bg-gray-50">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {columns.map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-4 rounded bg-gray-200 w-3/4" />
                        </td>
                      ))}
                    </tr>
                  ))
                : items.map((row, i) => (
                    <Fragment key={row.id}>
                      <tr
                        onClick={() =>
                          setExpandedId(expandedId === row.id ? null : row.id)
                        }
                        className={`cursor-pointer transition-colors ${
                          i % 2 === 1 ? 'bg-gray-50/50' : ''
                        } hover:bg-primary-50/50`}
                      >
                        {columns.map((col) => (
                          <td
                            key={col.key}
                            className="px-4 py-3 text-text-primary whitespace-nowrap"
                          >
                            {col.render
                              ? col.render(row)
                              : ((row as unknown as Record<string, unknown>)[col.key] as string) ?? '-'}
                          </td>
                        ))}
                      </tr>
                      {expandedId === row.id && row.stack && (
                        <tr>
                          <td
                            colSpan={columns.length}
                            className="bg-gray-900 px-4 py-3"
                          >
                            <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-green-400 font-mono leading-relaxed">
                              {row.stack}
                            </pre>
                            {row.metadata && (
                              <div className="mt-2 border-t border-gray-700 pt-2">
                                <p className="text-xs font-semibold text-gray-400 mb-1">
                                  {t('admin.errors.metadataLabel')}
                                </p>
                                <pre className="text-xs text-gray-300 font-mono">
                                  {JSON.stringify(row.metadata, null, 2)}
                                </pre>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                      {expandedId === row.id && !row.stack && (
                        <tr>
                          <td
                            colSpan={columns.length}
                            className="bg-gray-50 px-4 py-3 text-sm text-text-secondary italic"
                          >
                            {t('admin.errors.noStackTrace')}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-text-secondary">
            {t('admin.common.pageOf').replace('{page}', String(page)).replace('{total}', String(totalPages))} ({t('admin.common.resultsCount').replace('{count}', String(total))})
          </p>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setParam('page', String(page - 1))}
              className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.common.previous')}
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setParam('page', String(page + 1))}
              className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('admin.common.next')}
            </button>
          </div>
        </div>
      )}

      {/* Cleanup confirm dialog */}
      <ConfirmDialog
        open={cleanupOpen}
        title={t('admin.errors.cleanupTitle')}
        message={t('admin.errors.cleanupMessage')}
        confirmLabel={t('admin.errors.cleanupConfirm')}
        variant="danger"
        isLoading={cleanupLoading}
        onConfirm={handleCleanup}
        onCancel={() => setCleanupOpen(false)}
      />
    </div>
  );
}
